import { SegmentedControl, TextField } from '@genesislcap/foundation-zero';
import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { ICommonService } from '../../../../services/common.service';
import { CASCADE_ENUM, IEnumService } from '../../../../services/enum.service';
import { IHubZoneMatrix, Project } from '../../../../types';
import { CONTRACT_TYPE, TECHNOLOGY, VPPA_RFQ_STATUS } from '../../../../utils';
import { clearErrorUtil, displayErrorUtil } from '../../../../utils/form';
import { formatCriteriaDateMinutesPrecisionFromYear } from '../../../../utils/formatting';
import { IMultiSelectOption, MultiSelect } from '../../../common/multi-select/multi-select';
import { SellerRfqQueryStyles as styles } from './seller-rfq-query.style';
import { SellerRfqQueryTemplate as template } from './seller-rfq-query.template';

const name = 'seller-rfq-query';

@customElement({
  name,
  template,
  styles,
})
export class SellerRfqQuery extends FASTElement {
  @ICommonService commonService: ICommonService;
  @IEnumService enumService: IEnumService;

  @attr projectSelected: Project;

  @observable isPositionRelative: boolean = true;

  @observable techTypeValue: 'WIND' | 'SOLAR';
  @observable contractTypeValue: CONTRACT_TYPE;
  @observable priceMinValue: number;
  @observable priceMaxValue: number;
  @observable volumeMinValue: number;
  @observable volumeMaxValue: number;
  @observable buyerPostCollateralMinValue: number;
  @observable buyerPostCollateralMaxValue: number;
  @observable expectedSettlementMinValue: number;
  @observable expectedSettlementMaxValue: number;
  @observable downsideRiskMinValue: number;
  @observable downsideRiskMaxValue: number;
  @observable historicalNodalLMEMinValue: number;
  @observable historicalNodalLMEMaxValue: number;
  @observable impliedCarbonPriceMinValue: number;
  @observable impliedCarbonPriceMaxValue: number;
  @observable tenorDateMinValue: number;
  @observable tenorDateMaxValue: number;
  @observable selectedIso = [];
  @observable disableByIsoSelected = true;
  @observable disableByRecSelected = false;

  @observable _iso: Array<string> = [];
  @observable _isoList: Array<IMultiSelectOption> = [];

  @observable _settlementPoint: Array<string> = [];
  @observable _settlementPointList: Array<IMultiSelectOption> = [];

  @observable isoElement: MultiSelect;
  @observable recElement: MultiSelect;
  @observable technologyTypeElement: SegmentedControl;
  @observable settlementPointsElement: MultiSelect;
  @observable contractTypeElement: MultiSelect;
  @observable negativePricingMethodElement: MultiSelect;
  @observable alternativePricingStructuresElement: MultiSelect;
  @observable performanceGuaranteeMethodElement: MultiSelect;
  @observable priceMinValueElement: TextField;
  @observable priceMaxValueElement: TextField;
  @observable tenorDateMinValueElement: TextField;
  @observable tenorDateMaxValueElement: TextField;
  @observable volumeMinValueElement: TextField;
  @observable volumeMaxValueElement: TextField;
  @observable buyerPostCollateralMinValueElement: TextField;
  @observable buyerPostCollateralMaxValueElement: TextField;
  @observable expectedSettlementMinValueElement: TextField;
  @observable expectedSettlementMaxValueElement: TextField;
  @observable downsideRiskMinValueElement: TextField;
  @observable downsideRiskMaxValueElement: TextField;
  @observable historicalNodalLMEMinValueElement: TextField;
  @observable historicalNodalLMEMaxValueElement: TextField;
  @observable impliedCarbonPriceMinValueElement: TextField;
  @observable impliedCarbonPriceMaxValueElement: TextField;

  private _hubZoneMatrix: Array<IHubZoneMatrix> = [];

  private currentYear = new Date().getFullYear();
  private minYear = this.currentYear - 20;
  private maxYear = this.currentYear + 20;

  connectedCallback() {
    super.connectedCallback();
    this.getAllIso();
    this.loadEnums();
    this.createEventListeners();
  }

  projectSelectedChanged(oldValue, newValue: Project) {
    this.clearFilters();
    if (newValue) {
      this.techTypeValue = newValue.TECHNOLOGY;
      this.generateISODD(newValue.ISO);
      this.sendCriteria();
    }
  }

  // CRITERIA

  public sendCriteria() {
    let criteria = `(RFQ_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || RFQ_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}') && `;

    if (this.techTypeValue) {
      criteria += `(TECHNOLOGY == '${TECHNOLOGY.BOTH}' || TECHNOLOGY == '${this.techTypeValue}') && `;
    }

    const selectedIsos = this.isoElement.getSelectedIds();
    criteria += this.getMultiSelectCriteria(selectedIsos, 'ISO');

    const selectedSettlementPoints = this.settlementPointsElement.getSelectedIds();
    criteria += this.getMultiSelectCriteria(selectedSettlementPoints, 'SETTLEMENT_POINT');

    const contractTypes = this.contractTypeElement.getSelectedIds();
    criteria += this.getMultiSelectCriteria(contractTypes, CASCADE_ENUM.CONTRACT_TYPE);

    const applicableRecProgram = this.recElement.getSelectedIds();
    if (applicableRecProgram.length > 0){
        applicableRecProgram.push(
            'ANY'
        )
    }
    criteria += this.getMultiSelectCriteria(applicableRecProgram, CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM)

    const negativePricingMethods = this.negativePricingMethodElement.getSelectedIds();
    criteria += this.getMultiSelectCriteria(
      negativePricingMethods,
      CASCADE_ENUM.RFQ_NEGATIVE_PRICING_METHOD
    );

    const alternativePricingStructures = this.alternativePricingStructuresElement.getSelectedIds();
    criteria += this.getMultiSelectCriteria(
      alternativePricingStructures,
      CASCADE_ENUM.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHOD
    );

    const performanceGuaranteeMethod = this.performanceGuaranteeMethodElement.getSelectedIds();
    criteria += this.getMultiSelectCriteria(
      performanceGuaranteeMethod,
      CASCADE_ENUM.RFQ_PERFORMANCE_GUARANTEE_METHOD
    );

    criteria += this.getNumberRangeCriteria(
      this.priceMinValue,
      this.priceMaxValue,
      'FIXED_PRICE_LOWER_BOUND',
      'FIXED_PRICE_UPPER_BOUND'
    );

    criteria += this.getNumberRangeCriteria(
      this.volumeMinValue,
      this.volumeMaxValue,
      'REQUESTED_VOLUME',
      null
    );

    if (this.buyerPostCollateralMinValue || this.buyerPostCollateralMaxValue) {
      criteria += `( BUYER_POST_COD_COLLATERAL == 0 || `;
      criteria += this.getNumberRangeCriteria(
        this.buyerPostCollateralMinValue,
        this.buyerPostCollateralMaxValue,
        'BUYER_POST_COD_COLLATERAL',
        null
      );
      criteria = this.removeUnusedAnd(criteria);
      criteria += `) && `;
    }

    criteria += this.getNumberRangeCriteria(
      this.expectedSettlementMinValue,
      this.expectedSettlementMaxValue,
      'EXPECTED_SETTLEMENT_MIN',
      'EXPECTED_SETTLEMENT_MAX'
    );

    criteria += this.getNumberRangeCriteria(
      this.downsideRiskMinValue,
      this.downsideRiskMaxValue,
      'DOWNSIDE_RISK_MIN',
      'DOWNSIDE_RISK_MAX'
    );

    criteria += this.getNumberRangeCriteria(
      this.historicalNodalLMEMinValue,
      this.historicalNodalLMEMaxValue,
      'NODAL_LME_HISTORIC_MIN',
      'NODAL_LME_HISTORIC_MAX'
    );

    criteria += this.getNumberRangeCriteria(
      this.impliedCarbonPriceMinValue,
      this.impliedCarbonPriceMaxValue,
      'IMPLIED_CARBON_PRICE_MIN',
      'IMPLIED_CARBON_PRICE_MAX'
    );

    criteria += this.getDateRangeCriteria(
      this.tenorDateMinValue,
      this.tenorDateMaxValue,
      'TENOR_START_DATE'
    );

    criteria = this.removeUnusedAnd(criteria);

    this.$emit('updateCriteria', criteria);
  }

  private getDateRangeCriteria(minValue: number, maxValue: number, fieldName: string) {
    let criteria = ``;
    if (minValue || maxValue) {
      criteria += `(`;
      if (minValue) {
        const formatedStartDate = formatCriteriaDateMinutesPrecisionFromYear(minValue, true);
        criteria += `Expr.dateIsGreaterEqual(${fieldName},'${formatedStartDate}') && `;
      }
      if (maxValue) {
        const formatedEndDate = formatCriteriaDateMinutesPrecisionFromYear(maxValue, false);
        criteria += `Expr.dateIsLessEqual(${fieldName},'${formatedEndDate}') && `;
      }
      criteria = this.removeUnusedAnd(criteria);
      criteria += `) && `;
    }
    return criteria;
  }

  private getNumberRangeCriteria(
    minValue: number,
    maxValue: number,
    minFieldName: string,
    maxFieldName: string
  ) {
    let criteria = ``;
    if (minValue || maxValue) {
      criteria += `(`;
      if (minValue) {
        criteria += `(`;
        criteria += `${minFieldName} >= ${minValue}`;
        if (maxFieldName) {
          criteria += ` || ${maxFieldName} >= ${minValue}`;
        }
        criteria += `) && `;
      }
      if (maxValue) {
        criteria += `(`;
        criteria += `${minFieldName} <= ${maxValue}`;
        if (maxFieldName) {
          criteria += ` || ${maxFieldName} <= ${maxValue}`;
        }
        criteria += `) && `;
      }
      criteria = this.removeUnusedAnd(criteria);
      criteria += `) && `;
    }
    return criteria;
  }

  private getMultiSelectCriteria(list: Array<string>, fieldName: string): string {
    let criteria = ``;
    if (list?.length > 0) {
      criteria += `(`;
      list.forEach((element) => {
        criteria += `${fieldName} == '${element}' || `;
      });
      criteria = this.removeUnusedAnd(criteria);
      criteria += `) && `;
    }
    return criteria;
  }

  private removeUnusedAnd(criteria: string): string {
    return criteria.slice(0, -4);
  }

  // DATA SELECTION FUNCTIONS

  private createEventListeners() {
    this.priceMinValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(this.priceMinValue, this.priceMaxValue, this.priceMinValueElement)
    );
    this.priceMaxValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(this.priceMinValue, this.priceMaxValue, this.priceMinValueElement)
    );

    this.volumeMinValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.volumeMinValue,
        this.volumeMaxValue,
        this.volumeMinValueElement
      )
    );
    this.volumeMaxValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.volumeMinValue,
        this.volumeMaxValue,
        this.volumeMinValueElement
      )
    );

    this.buyerPostCollateralMinValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.buyerPostCollateralMinValue,
        this.buyerPostCollateralMaxValue,
        this.buyerPostCollateralMinValueElement
      )
    );
    this.buyerPostCollateralMaxValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.buyerPostCollateralMinValue,
        this.buyerPostCollateralMaxValue,
        this.buyerPostCollateralMinValueElement
      )
    );

    this.expectedSettlementMinValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.expectedSettlementMinValue,
        this.expectedSettlementMaxValue,
        this.expectedSettlementMinValueElement
      )
    );
    this.expectedSettlementMaxValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.expectedSettlementMinValue,
        this.expectedSettlementMaxValue,
        this.expectedSettlementMinValueElement
      )
    );

    this.downsideRiskMinValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.downsideRiskMinValue,
        this.downsideRiskMaxValue,
        this.downsideRiskMinValueElement
      )
    );
    this.downsideRiskMaxValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.downsideRiskMinValue,
        this.downsideRiskMaxValue,
        this.downsideRiskMinValueElement
      )
    );

    this.historicalNodalLMEMinValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.historicalNodalLMEMinValue,
        this.historicalNodalLMEMaxValue,
        this.historicalNodalLMEMinValueElement
      )
    );
    this.historicalNodalLMEMaxValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.historicalNodalLMEMinValue,
        this.historicalNodalLMEMaxValue,
        this.historicalNodalLMEMinValueElement
      )
    );

    this.impliedCarbonPriceMinValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.impliedCarbonPriceMinValue,
        this.impliedCarbonPriceMaxValue,
        this.impliedCarbonPriceMinValueElement
      )
    );
    this.impliedCarbonPriceMaxValueElement.addEventListener('blur', () =>
      this.checkNumberRangeRules(
        this.impliedCarbonPriceMinValue,
        this.impliedCarbonPriceMaxValue,
        this.impliedCarbonPriceMinValueElement
      )
    );

    this.tenorDateMinValueElement.addEventListener('blur', () => this.checkTenorDateRules());
    this.tenorDateMaxValueElement.addEventListener('blur', () => this.checkTenorDateRules());
    this.tenorDateMinValueElement.addEventListener('change', () => this.checkMinTenorDate());
    this.tenorDateMaxValueElement.addEventListener('change', () => this.checkMinTenorDate());
    this.contractTypeElement.addEventListener('change', () => this.disableComponents());
    this.contractTypeElement.addEventListener('blur', () => this.disableComponents());
  }

  private checkNumberRangeRules(minValue: number, maxValue: number, element: TextField): boolean {
    let isValid: boolean = true;
    if (minValue && maxValue && +minValue >= +maxValue) {
      displayErrorUtil(element, `Max value must be greater than Min value`);
      isValid = false;
    } else {
      clearErrorUtil(element);
    }
    return isValid;
  }

  private disableComponents() {
    const contractTypes = this.contractTypeElement.getSelectedIds();
    if (contractTypes.length === 1 && contractTypes[0] === "REC") {
        this.disableByRecSelected = true;
        this.expectedSettlementMinValue = null;
        this.expectedSettlementMaxValue = null;
        this.downsideRiskMinValue = null;
        this.downsideRiskMaxValue = null;
        this.performanceGuaranteeMethodElement.setDataList([]);
        this.negativePricingMethodElement.setDataList([]);
        this.alternativePricingStructuresElement.setDataList([]);
        this.loadSpecificEnum(
          this.negativePricingMethodElement,
          CASCADE_ENUM.RFQ_NEGATIVE_PRICING_METHOD
        );
        this.loadSpecificEnum(
          this.alternativePricingStructuresElement,
          CASCADE_ENUM.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHOD
        );
        this.loadSpecificEnum(
          this.performanceGuaranteeMethodElement,
          CASCADE_ENUM.RFQ_PERFORMANCE_GUARANTEE_METHOD
        );
    } else {
        this.disableByRecSelected = false;
    }
  }

  private checkTenorDateRules(): boolean {
    let isValid: boolean = true;

    if (
      this.tenorDateMinValue &&
      this.tenorDateMaxValue &&
      +this.tenorDateMinValue > +this.tenorDateMaxValue
    ) {
      displayErrorUtil(
        this.tenorDateMinValueElement,
        'End Year must be greater than or equal to Start Year'
      );
      isValid = false;
    } else {
      clearErrorUtil(this.tenorDateMinValueElement);
    }
    return isValid;
  }

  private checkMinTenorDate() {
    if (
      (this.tenorDateMinValue && this.tenorDateMinValue < this.minYear) ||
      this.tenorDateMinValue > this.maxYear
    ) {
      this.tenorDateMinValue = this.currentYear;
    }

    if (
      (this.tenorDateMaxValue && this.tenorDateMaxValue < this.minYear) ||
      this.tenorDateMaxValue > this.maxYear
    ) {
      if (this.tenorDateMinValue) {
        this.tenorDateMaxValue = this.tenorDateMinValue;
      } else {
        this.tenorDateMaxValue = this.currentYear;
      }
    }
  }

  public isoSelectionChanged(data) {
    this.selectedIso = data;
    if (this.selectedIso.length > 0) {
      this.disableByIsoSelected = false;
      this.generateSettlementPointDD();
    } else {
      this.disableByIsoSelected = true;
      this._settlementPoint = [];
      this._settlementPointList = [];
      this.settlementPointsElement.setDataList(this._settlementPointList);
    }
  }

  // LOAD DATA FUNCTIONS

  private getAllIso() {
    this.commonService.getHubZoneMatrix().then((response) => {
      if (response.REPLY) {
        this._hubZoneMatrix = response.REPLY;
        this.generateISODD();
      }
    });
  }

  private loadEnums() {
    this.loadSpecificEnum(this.contractTypeElement, CASCADE_ENUM.CONTRACT_TYPE);
    this.loadSpecificEnum(
      this.recElement,
      CASCADE_ENUM.APPLICABLE_REC_PROGRAM
    );
    this.loadSpecificEnum(
      this.negativePricingMethodElement,
      CASCADE_ENUM.RFQ_NEGATIVE_PRICING_METHOD
    );
    this.loadSpecificEnum(
      this.alternativePricingStructuresElement,
      CASCADE_ENUM.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHOD
    );
    this.loadSpecificEnum(
      this.performanceGuaranteeMethodElement,
      CASCADE_ENUM.RFQ_PERFORMANCE_GUARANTEE_METHOD
    );
  }

  private loadSpecificEnum(element: MultiSelect, enumReference: CASCADE_ENUM) {
    this.enumService.getFilteredEnumValues(enumReference).then((response) => {
      if (response.REPLY) {
        const enumList = [
          ...new Set(
            response.REPLY.map((item) => {
              const value: IMultiSelectOption = {
                CHECKED: false,
                ITEM_ID: item.ENUM_VALUE,
                ITEM_DESC: item.READABLE_VALUE,
                DISABLED: false,
                CATEGORY: '',
              };
              return value;
            })
          ),
        ];
        element.setDataList(enumList);
      }
    });
  }

  private generateSettlementPointDD() {
    // Get all rows with selected ISO
    const selectedPoints = this._hubZoneMatrix.filter((item) =>
      this.selectedIso.find((element) => item.ISO === element)
    );

    // Get Unique Settlement Points for DD
    this._settlementPoint = [...new Set(selectedPoints.map((item) => item.SETTLEMENT_POINT))];
    this._settlementPointList = [
      ...new Set(
        this._settlementPoint.map((item) => {
          const value: IMultiSelectOption = {
            CHECKED: false,
            ITEM_ID: item,
            ITEM_DESC: item,
            DISABLED: false,
            CATEGORY: '',
          };
          return value;
        })
      ),
    ];

    this.settlementPointsElement.setDataList(this._settlementPointList);
  }

  private generateISODD(iso?: string) {
    this._iso = [...new Set(this._hubZoneMatrix.map((item) => item.ISO))];
    this._isoList = [
      ...new Set(
        this._iso.map((item) => {
          const selectedIso = iso && iso === item;
          const value: IMultiSelectOption = {
            CHECKED: selectedIso ? true : false,
            ITEM_ID: item,
            ITEM_DESC: item,
            DISABLED: selectedIso ? true : false,
            CATEGORY: '',
          };
          return value;
        })
      ),
    ];
    this.isoElement.setDataList(this._isoList);
  }

  // Clear filters
  public clearFilters() {
    this.contractTypeValue = null;
    this.priceMinValue = null;
    this.priceMaxValue = null;
    this.volumeMinValue = null;
    this.volumeMaxValue = null;
    this.buyerPostCollateralMinValue = null;
    this.buyerPostCollateralMaxValue = null;
    this.expectedSettlementMinValue = null;
    this.expectedSettlementMaxValue = null;
    this.downsideRiskMinValue = null;
    this.downsideRiskMaxValue = null;
    this.historicalNodalLMEMinValue = null;
    this.historicalNodalLMEMaxValue = null;
    this.impliedCarbonPriceMinValue = null;
    this.impliedCarbonPriceMaxValue = null;
    this.tenorDateMinValue = null;
    this.tenorDateMaxValue = null;
    this.selectedIso = [];

    this.isoElement.setDataList([]);
    this.settlementPointsElement.setDataList([]);
    this.recElement.setDataList([]);
    this.contractTypeElement.setDataList([]);
    this.negativePricingMethodElement.setDataList([]);
    this.alternativePricingStructuresElement.setDataList([]);
    this.performanceGuaranteeMethodElement.setDataList([]);
    this.loadEnums();
    if (this.projectSelected) {
      this.techTypeValue = this.projectSelected.TECHNOLOGY;
      this.generateISODD(this.projectSelected.ISO);
      this.sendCriteria();
    } else {
      this.generateISODD();
    }

    this.$emit('updateCriteria', '');
  }
}
